import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';

function readPolicy(response) {
  if (typeof response?.enabled !== 'boolean' || typeof response?.depositApprovalRequired !== 'boolean'
    || !Array.isArray(response?.stableParityAssets)
    || response.stableParityAssets.some((asset) => !['USDT', 'USDC'].includes(asset))) {
    throw new Error('Invalid payment policy response.');
  }
  return { enabled: response.enabled, depositApprovalRequired: response.depositApprovalRequired, stableParityAssets: [...new Set(response.stableParityAssets)].sort() };
}

export default function PaymentPolicySettings() {
  const { token, hasPermission } = useAuth();
  const { t } = useTranslation();
  const [policy, setPolicy] = useState(null);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);
  const generation = useRef(0);
  const savingRef = useRef(false);
  const canManage = hasPermission('settings.manage');
  const canView = hasPermission('settings.view');

  useEffect(() => {
    const current = ++generation.current;
    setPolicy(null); setSaved(null); setError(''); setSuccess(false);
    savingRef.current = false; setSaving(false);
    if (canView) {
      adminApi.getPaymentExecutionSettings(token).then((response) => {
        if (generation.current !== current) return;
        const next = readPolicy(response); setPolicy(next); setSaved(next);
      }).catch((failure) => {
        if (generation.current === current) setError(failure.message || 'Failed to load payment policy.');
      });
    }
    return () => { generation.current += 1; };
  }, [token, canView, reload]);

  async function save(event) {
    event.preventDefault();
    if (!canManage || !policy || savingRef.current) return;
    const current = generation.current;
    savingRef.current = true; setSaving(true); setError(''); setSuccess(false);
    try {
      const response = await adminApi.updatePaymentExecutionSettings(token, policy);
      if (generation.current !== current) return;
      const next = readPolicy(response); setPolicy(next); setSaved(next); setSuccess(true);
    } catch (failure) {
      if (generation.current === current) setError(failure.message || 'Failed to save payment policy.');
    } finally {
      if (generation.current === current) { savingRef.current = false; setSaving(false); }
    }
  }

  if (!canView) return null;
  const disabled = !canManage || saving || !policy;
  const changed = JSON.stringify(policy) !== JSON.stringify(saved);
  function toggle(name, checked) {
    if (disabled) return;
    setSuccess(false);
    setPolicy((previous) => ['USDT', 'USDC'].includes(name)
      ? { ...previous, stableParityAssets: checked ? [...previous.stableParityAssets, name].sort() : previous.stableParityAssets.filter((asset) => asset !== name) }
      : { ...previous, [name]: checked });
  }
  const controls = [
    ['enabled', t('paymentPolicy.enabled', { defaultValue: 'Allow new payments' }), policy?.enabled],
    ['depositApprovalRequired', t('paymentPolicy.approval', { defaultValue: 'Require operator approval for deposits' }), policy?.depositApprovalRequired],
    ...['USDT', 'USDC'].map((asset) => [asset, t('paymentPolicy.parity', { defaultValue: `Value 1 ${asset} as 1 USD`, asset }), policy?.stableParityAssets.includes(asset)]),
  ];
  return (
    <section className="card card-body mt-3" aria-labelledby="payment-policy-title">
      <h2 className="h5" id="payment-policy-title">{t('paymentPolicy.title', { defaultValue: 'Payment policy' })}</h2>
      <p className="text-muted">{t('paymentPolicy.help', { defaultValue: 'Applies to new payments. Existing requests keep their approved amounts and valuation. Disabling new payments does not stop processing existing requests.' })}</p>
      <p className="text-muted">{t('paymentPolicy.parityHelp', { defaultValue: 'Enable a fixed USD valuation only for assets your business accepts at that value. Other assets require a configured exchange rate.' })}</p>
      {error && <div role="alert" className="alert alert-danger">{error}</div>}
      {success && <div role="status" className="alert alert-success">{t('paymentPolicy.saved', { defaultValue: 'Payment policy saved.' })}</div>}
      <form onSubmit={save}>
        {!policy && !error && <p role="status">{t('paymentPolicy.loading', { defaultValue: 'Loading payment policy…' })}</p>}
        {policy && controls.map(([name, label, checked]) => (
          <div className="form-check mb-2" key={name}>
            <input id={`payment-policy-${name}`} name={name} type="checkbox" className="form-check-input" checked={Boolean(checked)} disabled={disabled} onChange={(event) => toggle(name, event.target.checked)} />
            <label className="form-check-label" htmlFor={`payment-policy-${name}`}>{label}</label>
          </div>
        ))}
        {!policy && error && <button type="button" className="btn btn-default" onClick={() => setReload((value) => value + 1)}>{t('paymentPolicy.retry', { defaultValue: 'Retry' })}</button>}
        {canManage && policy && <div className="d-flex justify-content-end gap-2 mt-3">
          <button type="button" className="btn btn-default" disabled={disabled || !changed} onClick={() => { setPolicy(saved); setError(''); setSuccess(false); }}>{t('paymentPolicy.reset', { defaultValue: 'Reset' })}</button>
          <button type="submit" className="btn btn-primary" disabled={disabled || !changed}>{t('paymentPolicy.save', { defaultValue: 'Save payment policy' })}</button>
        </div>}
      </form>
    </section>
  );
}
